import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green, orange, blue, red, purple, deepPurple } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Colorize, Palette, Gradient } from "@material-ui/icons";
import { ColorBox } from "material-ui-color";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { 
  FormControl, 
  IconButton, 
  InputAdornment, 
  InputLabel, 
  MenuItem, 
  Select,
  Typography,
  Popover,
  Grid,
  useMediaQuery,
  useTheme,
  Fade
} from "@material-ui/core";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import Draggable from 'react-draggable';
import Paper from '@material-ui/core/Paper';
import { Slide } from '@material-ui/core';


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialogPaper: {
    borderRadius: "20px",
    background: theme.palette.background.paper,
    overflow: "hidden",
    [theme.breakpoints.down('sm')]: {
      minWidth: "95%",
      maxWidth: "95%",
      margin: theme.spacing(1),
    },
  },
  dialogTitle: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: "16px 20px",
    textAlign: "center",
    fontSize: "1.25rem",
    fontWeight: 600,
    cursor: "move",
    [theme.breakpoints.down('sm')]: {
      padding: "12px 16px",
      fontSize: "1.1rem",
    },
  },
  dialogContent: {
    padding: theme.spacing(4),
    background: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      padding: "16px",
    },
  },
  dialogActions: {
    padding: theme.spacing(3),
    background: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      gap: theme.spacing(1),
      padding: "12px",
    },
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      '& > *:not(:last-child)': {
        marginRight: 0,
        marginBottom: theme.spacing(2),
      },
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: 120,
    background: theme.palette.background.paper,
    borderRadius: "8px",
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  colorAdorment: {
    width: 24,
    height: 24,
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  cancelButton: {
    background: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  saveButton: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  inputField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: "12px",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: theme.palette.background.default,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
      },
    },
    "& .MuiInputLabel-outlined": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  colorPickerPopover: {
    marginTop: theme.spacing(1),
    borderRadius: "12px",
    boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      left: '5% !important',
    },
  },
  colorPickerContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    color: theme.palette.primary.dark,
    fontWeight: 600,
    margin: "16px 0 8px 0",
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: "8px",
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      margin: "12px 0 6px 0",
    },
  },
  backgroundPattern: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 0.05,
    zIndex: 0,
    backgroundImage: "radial-gradient(#6517ab1px, transparent 1px)",
    backgroundSize: "20px 20px",
  },
  // Novas classes para a barra de rolagem
  scrollableContent: {
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
      '&:hover': {
        background: '#555',
      },
    },
    [theme.breakpoints.down('sm')]: {
      maxHeight: 'calc(100vh - 180px)',
    },
  },
  fixedActions: {
    position: 'sticky',
    bottom: 0,
    background: theme.palette.background.default,
    zIndex: 2,
  },
}));

const TagSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Mensagem muito curta")
    .required("Obrigatório"),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DraggablePaper = (props) => (
  <Draggable
    handle="#draggable-dialog-title"
    cancel={'[class*="MuiDialogContent-root"], [class*="MuiDialogActions-root"]'}
    bounds="parent"
  >
    <Paper {...props} />
  </Draggable>
);

const TagModal = ({ open, onClose, tagId, kanban }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLane, setSelectedLane] = useState([]);
  const [selectedRollbackLane, setSelectedRollbackLane] = useState([]);
  
  const initialState = {
    name: "",
    color: getRandomHexColor(),
    kanban: kanban,
    timeLane: 0,
    nextLaneId: 0,
    greetingMessageLane: "",
    rollbackLaneId: 0,
  };
  const [tag, setTag] = useState(initialState);

  const handleColorClick = (event) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const handleColorChange = (val) => {
    setTag((prev) => ({
      ...prev,
      color: `#${val.hex}`,
    }));
  };

  const colorPickerOpen = Boolean(colorAnchorEl);
  const colorPickerId = colorPickerOpen ? 'color-picker-popover' : undefined;

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { kanban: 1, tagId },
          });
          setLanes(data.tags);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, []);

  useEffect(() => {
    try {
      (async () => {
        if (!tagId) return;
        const { data } = await api.get(`/tags/${tagId}`);
        setTag((prevState) => {
          return { ...prevState, ...data };
        });
        if (data.nextLaneId) {
          setSelectedLane(data.nextLaneId);
        }
        if (data.rollbackLaneId) {
          setSelectedRollbackLane(data.rollbackLaneId);
        }
      })();
    } catch (err) {
      toastError(err);
    }
  }, [tagId, open]);

  const handleClose = () => {
    setTag(initialState);
    setColorAnchorEl(null);
    onClose();
  };

  const handleSaveTag = async (values) => {
    const tagData = {
      ...values,
      userId: user?.id,
      kanban: kanban,
      nextLaneId: selectedLane || null,
      rollbackLaneId: selectedRollbackLane || null,
    };
    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, tagData);
      } else {
        await api.post("/tags", tagData);
      }
      toast.success(
        kanban === 0
          ? `${i18n.t("tagModal.success")}`
          : `${i18n.t("tagModal.successKanban")}`
      );
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  function getRandomHexColor() {
    const colors = ['#6517ab', '#764ba2', '#6B8DD6', '#8E37D7', '#5F72BE', '#9F7AEA', '#6517ab'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
      >
          <div className={classes.backgroundPattern} />
          
          <DialogTitle 
            className={classes.dialogTitle}
            id={isMobile ? undefined : "draggable-dialog-title"}
          >
            {tagId
              ? kanban === 0
                ? `${i18n.t("tagModal.title.edit")}`
                : `${i18n.t("tagModal.title.editKanban")}`
              : kanban === 0
              ? `${i18n.t("tagModal.title.add")}`
              : `${i18n.t("tagModal.title.addKanban")}`}
          </DialogTitle>
          
          <Formik
            initialValues={tag}
            enableReinitialize={true}
            validationSchema={TagSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSaveTag(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ touched, errors, isSubmitting, values }) => (
              <Form>
                <div className={classes.scrollableContent}>
                  <DialogContent className={classes.dialogContent}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                          <Palette /> Informações Básicas
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label={i18n.t("tagModal.form.name")}
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.inputField}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Gradient style={{ color: theme.palette.primary.light }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          label={i18n.t("tagModal.form.color")}
                          name="color"
                          id="color"
                          error={touched.color && Boolean(errors.color)}
                          helperText={touched.color && errors.color}
                          className={classes.inputField}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <div
                                  style={{ 
                                    backgroundColor: values.color,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                  }}
                                  className={classes.colorAdorment}
                                ></div>
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={handleColorClick}
                                style={{ color: theme.palette.primary.main }}
                                aria-describedby={colorPickerId}
                              >
                                <Colorize />
                              </IconButton>
                            ),
                          }}
                          variant="outlined"
                          margin="dense"
                        />
                        <Popover
                          id={colorPickerId}
                          open={colorPickerOpen}
                          anchorEl={colorAnchorEl}
                          onClose={handleColorClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                          }}
                          className={classes.colorPickerPopover}
                        >
                          <div className={classes.colorPickerContainer}>
                            <ColorBox
                              disableAlpha={true}
                              hslGradient={false}
                              value={tag.color}
                              onChange={handleColorChange}
                            />
                          </div>
                        </Popover>
                      </Grid>
                      
                      {kanban === 1 && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="h6" className={classes.sectionTitle}>
                              <Gradient /> Configurações de Kanban
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Field
                              as={TextField}
                              label={i18n.t("tagModal.form.timeLane")}
                              name="timeLane"
                              error={touched.timeLane && Boolean(errors.timeLane)}
                              helperText={touched.timeLane && errors.timeLane}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.inputField}
                              type="number"
                            />
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={`${classes.formControl} ${classes.inputField}`}
                            >
                              <InputLabel id="nextLaneId-label">
                                {i18n.t("tagModal.form.nextLaneId")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("tagModal.form.nextLaneId")}
                                labelId="nextLaneId-label"
                                id="nextLaneId"
                                name="nextLaneId"
                                error={touched.nextLaneId && Boolean(errors.nextLaneId)}
                                value={selectedLane}
                                onChange={(e) => setSelectedLane(e.target.value || null)}
                              >
                                <MenuItem value={null}>&nbsp;</MenuItem>
                                {lanes.map((lane) => (
                                  <MenuItem key={lane.id} value={lane.id}>
                                    {lane.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Field
                              as={TextField}
                              label={i18n.t("tagModal.form.greetingMessageLane")}
                              name="greetingMessageLane"
                              rows={isMobile ? 3 : 5}
                              multiline
                              error={
                                touched.greetingMessageLane &&
                                Boolean(errors.greetingMessageLane)
                              }
                              helperText={
                                touched.greetingMessageLane && errors.greetingMessageLane
                              }
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.inputField}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={`${classes.formControl} ${classes.inputField}`}
                            >
                              <InputLabel id="rollbackLaneId-label">
                                {i18n.t("tagModal.form.rollbackLaneId")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("tagModal.form.rollbackLaneId")}
                                labelId="rollbackLaneId-label"
                                id="rollbackLaneId"
                                name="rollbackLaneId"
                                error={
                                  touched.rollbackLaneId && Boolean(errors.rollbackLaneId)
                                }
                                value={selectedRollbackLane}
                                onChange={(e) => setSelectedRollbackLane(e.target.value)}
                              >
                                <MenuItem value={null}>&nbsp;</MenuItem>
                                {lanes.map((lane) => (
                                  <MenuItem key={lane.id} value={lane.id}>
                                    {lane.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </DialogContent>
                </div>
                
                <DialogActions className={`${classes.dialogActions} ${classes.fixedActions}`}>
                  <Button
                    onClick={handleClose}
                    startIcon={<CancelIcon />}
                    className={classes.cancelButton}
                    disabled={isSubmitting}
                    variant="contained"
                  >
                    {i18n.t("tagModal.buttons.cancel")}
                  </Button>
                  
                  <Button
                    type="submit"
                    startIcon={<SaveIcon />}
                    className={classes.saveButton}
                    disabled={isSubmitting}
                    variant="contained"
                  >
                    {tagId
                      ? `${i18n.t("tagModal.buttons.okEdit")}`
                      : `${i18n.t("tagModal.buttons.okAdd")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Dialog>
    </div>
  );
};

export default TagModal;